import type { ReactElement } from 'react';
import { useStatusEvidence, type StatusEvidence } from '@/hooks/useStatusEvidence';
import { useSubmissionViewState } from '@/hooks/useSubmissionWorkspace';
import { unavailableLifecycleStages } from '@/constants/lifecycle';
import { StatusPipeline } from './StatusPipeline';

export interface EvidenceStatusPipelineProps {
  readonly submissionId: string;
  readonly status?: string | null;
  readonly refreshToken?: unknown;
  readonly documentToken?: unknown;
  readonly metadataToken?: unknown;
  readonly evidence?: StatusEvidence;
  readonly onRefresh?: () => void;
}

/** Every stage and the current marker come from the same evidence snapshot. */
export function EvidenceStatusPipeline(props: EvidenceStatusPipelineProps): ReactElement {
  // Keyed remount: useSubmissionViewState seeds from the workspace map only
  // in its useState initializer, so switching submissions must remount the
  // view to read the new submission's stored collapse preference.
  return <EvidenceStatusPipelineView key={props.submissionId} {...props} />;
}

function EvidenceStatusPipelineView({ submissionId, status, refreshToken, documentToken, metadataToken, evidence, onRefresh }: EvidenceStatusPipelineProps): ReactElement {
  const [expanded, setExpanded] = useSubmissionViewState(submissionId, 'statusPipelineExpanded', false);
  const local = useStatusEvidence(evidence ? [] : [submissionId], refreshToken ?? status, documentToken, metadataToken);
  const current = evidence ?? local.get(submissionId);
  const stages = current.snapshot?.stages.map((stage) => ({
    ...stage, label: stage.key === 'validated' && stage.state !== 'done' ? 'Validation' : stage.label,
  })) ?? unavailableLifecycleStages(current.loading ? 'Checking status…' : 'Status unavailable');
  return <StatusPipeline stages={stages} currentStageKey={current.snapshot?.currentStageKey ?? ''}
    expanded={expanded} onExpandedChange={setExpanded}
    // No snapshot means no current stage: say so in the collapsed summary
    // (next to the retry action on error) rather than implying one.
    summary={current.snapshot ? undefined : (
      <span data-testid="status-pipeline-summary" style={{ fontSize: 13, color: '#6B7280' }}>
        {current.loading ? 'Checking status…' : 'Status unavailable'}
      </span>
    )}
    actions={current.error ? <button type="button" className="btn btn-ghost" onClick={onRefresh ?? local.refresh}>Retry status</button> : undefined} />;
}
